import makeStyles from '@mui/styles/makeStyles';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { mainColors } from '../../../../../styling/theme';
import { EditorTileProps } from '../../kids/editor/editorTiles/KidsEditorColumn.component';
import { FC } from 'react';
import KidsEditorGridItem from '../../kidsV2/layout/KidsEditorGridItem.component';
import CustomTable from '../../../../tables/CustomTable';
import UtilityButtons from '../buttons/UtilityButtons.component';
import { percentageToTwoDecimalPlaces } from '../../../../../utilities/numberFormatters';

const useStyles = makeStyles(() => ({
  textEditorBox: {
    marginTop: '1.25rem',
    marginBottom: '1.25rem',
    marginLeft: '0.62rem',
    fontSize: '0.94rem',
    boxSizing: 'border-box',
    padding: '0.62rem',
    borderRadius: '6px',
    width: '100%',
    minHeight: '6.25rem',
  },
  editorContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginRight: '0.62rem',
  },
  formatContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: '0.62rem',
    gap: '1.25rem',
  },
}));

interface CompositionOfCostsTableData {
  costs: string;
  description: string;
  value: string;
  secondaryDescription: string;
}

const generalCompositionOfCostsTableData: CompositionOfCostsTableData[] = [
  {
    costs: 'One-Off Costs',
    description: 'Entry costs',
    value: '',
    secondaryDescription: '',
  },
  {
    costs: '',
    description: 'Exit costs',
    value: '',
    secondaryDescription: '',
  },
  {
    costs: 'Ongoing Costs',
    description: 'Portfolio transaction costs',
    value: '',
    secondaryDescription: '',
  },
  {
    costs: '',
    description: 'Other ongoing costs',
    value: '',
    secondaryDescription: '',
  },
  {
    costs: 'Incidental Costs',
    description: 'Performance fees',
    value: '',
    secondaryDescription: '',
  },
  {
    costs: '',
    description: 'Carried interests',
    value: '',
    secondaryDescription: '',
  },
];

function buildCompositionOfCostsTableData(
  figures_data: any,
): CompositionOfCostsTableData[] {
  const costs = Array.isArray(figures_data?.costs) ? figures_data.costs : [];

  return [
    {
      costs: 'One-Off Costs',
      description: 'Entry costs',
      value: percentageToTwoDecimalPlaces(costs[0]),
      secondaryDescription:
        'The impact of the costs you pay when entering your investment.',
    },
    {
      costs: '',
      description: 'Exit costs',
      value: percentageToTwoDecimalPlaces(costs[1]),
      secondaryDescription:
        'The maximum impact of the costs of exiting your investment when it matures.',
    },
    {
      costs: 'Ongoing Costs',
      description: 'Portfolio transaction costs',
      value: percentageToTwoDecimalPlaces(costs[2]),
      secondaryDescription:
        'The impact of the costs of us buying and selling underlying investments for the product.',
    },
    {
      costs: '',
      description: 'Other ongoing costs',
      value: percentageToTwoDecimalPlaces(costs[3]),
      secondaryDescription:
        'The impact of the costs that we take each year for managing your investments.',
    },
    {
      costs: 'Incidental Costs',
      description: 'Performance fees',
      value:
        costs.length > 5 && costs[5] != null
          ? percentageToTwoDecimalPlaces(costs[5])
          : '0.00%',
      secondaryDescription: 'The impact of the performance fee. We take these from your investment if the product outperforms its high water mark, of (a) the highest previous net asset value per share of the series concerned at the end of a performance period; and (b) the initial issue price of the initial issue price of the shares of that series.',
    },
  ];
}

const columns: CustomColumn<CompositionOfCostsTableData>[] = [
  {
    title: '',
    field: 'costs',
    cellStyle: {
      width: '20%',
      border: '1px solid #1875d2',
      padding: '0.31rem',
      textAlign: 'center',
      backgroundColor: 'white',
    },
    headerStyle: {
      width: '20%',
      // border: '1px solid white',
      padding: '0.31rem',
      textAlign: 'center',
      color: 'white',
      backgroundColor: mainColors.controlButtonBlue,
    },
  },
  {
    title: '',
    field: 'description',
    cellStyle: {
      width: '20%',
      border: '1px solid #1875d2',
      padding: '0.31rem',
      textAlign: 'center',
      backgroundColor: 'white',
    },
    headerStyle: {
      width: '20%',
      padding: '0.31rem',
      textAlign: 'center',
      color: 'white',
      backgroundColor: mainColors.controlButtonBlue,
    },
  },
  {
    title: '',
    field: 'value',
    cellStyle: {
      border: '1px solid #1875d2',
      padding: '0.31rem',
      textAlign: 'center',
      backgroundColor: 'white',
    },
    headerStyle: {
      padding: '0.31rem',
      textAlign: 'center',
      color: 'white',
      backgroundColor: mainColors.controlButtonBlue,
    },
  },
  {
    title: '',
    field: 'secondaryDescription',
    cellStyle: {
      border: '1px solid #1875d2',
      padding: '0.31rem',
      textAlign: 'center',
      backgroundColor: 'white',
    },
    headerStyle: {
      // border: '1px solid white',
      padding: '0.31rem',
      textAlign: 'center',
      color: 'white',
      backgroundColor: mainColors.controlButtonBlue,
    },
  },
];

const CompositionOfCostsTableEditorTile: FC<EditorTileProps> = ({
  priipsKidsElement,
  moveField,
  deleteField,
}) => {
  // Store the index of the current element in the overall data
  const index = priipsKidsElement.kiidIndex;
  // Store the field id of the element
  const fieldId = priipsKidsElement.fieldId;
  // Convert the content to a javascript object
  const content = JSON.parse(priipsKidsElement.content);
  // Build the table data
  const tableData = buildCompositionOfCostsTableData(content);

  const classes = useStyles();

  return (
    <KidsEditorGridItem xs={12}>
      <div className={classes.editorContainer}>
        <div className={classes.textEditorBox}>
          <CustomTable<CompositionOfCostsTableData>
            columns={columns}
            id={'performance_scenarios_table'}
            showToolbar={false}
            data={tableData}
            options={{
              paging: false,
              search: false,
              exportButton: false,
              sorting: false,
              draggable: false,
              toolbar: false,
            }}
          />
        </div>
        <UtilityButtons
          moveField={moveField}
          index={index}
          deleteField={deleteField}
          fieldId={fieldId}
          disableDelete
          disableDeleteMessage="You cannot delete the composition of costs table."
        />
      </div>
    </KidsEditorGridItem>
  );
};

export default CompositionOfCostsTableEditorTile;
